import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users2, RefreshCw, AlertTriangle, Info, Calculator, FileUp, ListChecks, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AskEvaButton } from "@/components/AskEvaButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { SingletonSection, ListSection } from "@/components/social/SocialSections";
import KpiDrilldown from "@/components/social/KpiDrilldown";

const BASIS_LABELS = { HEADCOUNT: "Headcount (persone)", FTE: "Full-Time Equivalent" };

const CONTRACT_OPTIONS = [
  { value: "PERMANENT", label: "Permanent" }, { value: "TEMPORARY", label: "Temporary" },
  { value: "OTHER", label: "Altro" }, { value: "TO_REVIEW", label: "Da classificare" },
];
const GENDER_OPTIONS = [
  { value: "FEMALE", label: "Donne" }, { value: "MALE", label: "Uomini" },
  { value: "OTHER", label: "Altro" }, { value: "NOT_REPORTED", label: "Non dichiarato" },
];
const WORKER_OPTIONS = [
  { value: "SELF_EMPLOYED_EXCLUSIVE", label: "Autonomi esclusivi" },
  { value: "TEMPORARY_AGENCY_WORKER", label: "Lavoratori somministrati" },
  { value: "OTHER_NON_EMPLOYEE", label: "Altro non-dipendente" },
];
const DIMENSION_OPTIONS = [
  { value: "CONTRACT_TYPE", label: "Tipo di contratto" },
  { value: "GENDER", label: "Genere" },
  { value: "COUNTRY", label: "Paese del contratto" },
  { value: "WORKER_CATEGORY", label: "Non-dipendenti (C5)" },
];

const fmt = (v, suffix = "") => (v === null || v === undefined ? "—" : `${typeof v === "number" ? v.toLocaleString("it-IT", { maximumFractionDigits: 2 }) : v}${suffix}`);

export default function Social() {
  const { companyId, period, can, role } = useTenant();
  const [summary, setSummary] = useState(null);
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [tab, setTab] = useState("forza-lavoro");

  const canWrite = can("social:write");
  const canCalculate = can("esg:verify");
  const canImport = can("social:import");
  const canHR = can("social:hr:read") && role !== "SUPER_ADMIN";

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get(`/companies/${companyId}/periods/${period.id}/social/summary`),
        api.get(`/companies/${companyId}/periods/${period.id}/social/calculations`),
      ]);
      setSummary(s.data);
      setCalculations(c.data);
    } catch (err) {
      toast.error("Errore caricamento Social Engine", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  const calcByMetric = useMemo(() => {
    const m = {};
    calculations.forEach((c) => { if (!m[c.metric_code]) m[c.metric_code] = c; });
    return m;
  }, [calculations]);

  if (!period) return null;

  const s = summary;
  const canonicalValue = (code, fallback) => {
    const value = s?.canonical_values?.find((item) => item.datapoint_code === code)?.value;
    return value ?? fallback;
  };
  const kpis = s ? [
    { key: "headcount", label: "Dipendenti", value: canonicalValue("VSME.B8.HEADCOUNT.TOTAL", s.workforce.headcount_total), unit: "persone", source: s.workforce.source_type },
    { key: "fte", label: "FTE", value: s.workforce.fte_total, unit: "FTE", estimated: s.workforce.fte_estimated, source: s.workforce.source_type },
    { key: "turnover", label: "Turnover", value: s.turnover.rate, unit: "%", metric: "EMPLOYEE_TURNOVER_RATE", na: s.turnover.applicable === false, naReason: `Sotto soglia ${s.turnover.threshold} dipendenti` },
    { key: "accidents", label: "Infortuni registrabili", value: canonicalValue("VSME.B9.ACCIDENTS", s.safety.recordable_accidents), source: s.safety.source_type },
    { key: "accident_rate", label: "Tasso infortuni", value: s.safety.accident_rate, metric: "WORK_RELATED_ACCIDENT_RATE" },
    { key: "fatalities", label: "Decessi sul lavoro", value: canonicalValue("VSME.B9.FATALITIES", s.safety.fatalities_injuries) },
    { key: "paygap", label: "Gender pay gap", value: s.remuneration.gender_pay_gap, unit: "%", metric: "GENDER_PAY_GAP", omitted: s.remuneration.omitted_allowed, omissionThreshold: s.remuneration.omission_threshold },
    { key: "cb", label: "Copertura contrattazione", value: canonicalValue("VSME.B10.COLLECTIVE_BARGAINING_PCT", s.collective_bargaining.coverage_pct), unit: "%", metric: "COLLECTIVE_BARGAINING_COVERAGE", band: s.collective_bargaining.band },
    { key: "training", label: "Ore formazione medie", value: canonicalValue("VSME.B10.TRAINING_HOURS_AVG", s.training.average_hours), unit: "h/anno", metric: "TRAINING_HOURS_AVG" },
    { key: "mgmt", label: "Rapporto F/M management", value: s.management.ratio, metric: "MANAGEMENT_GENDER_RATIO", display: s.management.ratio_display },
  ] : [];

  const runImport = async (commit) => {
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/social/import`,
        { csv_text: csvText, commit });
      setImportPreview(data.preview);
      if (commit) {
        toast.success(`Import completato: ${data.applied} valori applicati`);
        setSummary(data.summary);
        load();
      }
    } catch (err) {
      toast.error("Errore import", { description: formatApiError(err) });
    }
  };

  return (
    <div className="space-y-6" data-testid="social-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Users2 className="w-7 h-7 text-sky-700" aria-hidden="true" /> Social Engine
          </h1>
          <AskEvaButton question="Mostrami i KPI Social del periodo e da quale fonte provengono." />
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl leading-relaxed">
            Motore Social indipendente dalla sorgente dati (manuale, CSV, JOBTIMEHR futuro).
            Dati aggregati per il periodo {period.year} → KPI verificabili → VSME B8, B9, B10, C5, C6, C7.
            Nessun dato nominativo viene memorizzato (privacy by design).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {s?.calculation_basis && (
            <Badge className="bg-sky-100 text-sky-900 border-sky-300" data-testid="basis-badge">
              Base report: {BASIS_LABELS[s.calculation_basis] || s.calculation_basis}
            </Badge>
          )}
          {canImport && (
            <Button variant="outline" onClick={() => { setImportOpen(true); setImportPreview(null); }} data-testid="social-import-button">
              <FileUp className="w-4 h-4 mr-1" aria-hidden="true" /> Importa CSV
            </Button>
          )}
          <Link to="/social/review">
            <Button variant="outline" data-testid="social-review-link">
              <ListChecks className="w-4 h-4 mr-1" aria-hidden="true" />
              {role === "DATA_ENTRY" ? "Riepilogo dati" : "Revisione dati"}
            </Button>
          </Link>
          {canCalculate && (
            <Button variant="outline" onClick={load} data-testid="social-recalc-button">
              <RefreshCw className="w-4 h-4 mr-1" aria-hidden="true" /> Ricalcola
            </Button>
          )}
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500">Caricamento in corso…</p>}

      {s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="social-kpi-strip">
            {kpis.map((k) => (
              <Card key={k.key} className="border-slate-200" data-testid={`kpi-${k.key}`}>
                <CardContent className="p-3.5">
                  <p className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">{k.label}</p>
                  <p className="text-xl font-extrabold text-slate-900 mt-1" data-testid={`kpi-${k.key}-value`}>
                    {k.na ? (
                      <span className="text-sm font-semibold text-slate-500">Non applicabile</span>
                    ) : (k.value === null || k.value === undefined) ? (
                      <span className="text-sm font-semibold text-slate-500">Dato non inserito</span>
                    ) : fmt(k.value, k.unit ? ` ${k.unit}` : "")}
                  </p>
                  {!k.na && (k.value === null || k.value === undefined) && (
                    <Link to="/questionario" className="inline-block text-[11px] font-semibold text-green-700 hover:underline mt-0.5"
                      data-testid={`kpi-${k.key}-goto-question`}>
                      Completa nel Questionario ESG
                    </Link>
                  )}
                  {k.display && <p className="text-xs text-slate-500">{k.display}</p>}
                  {k.band && <p className="text-xs text-slate-500">Fascia {k.band}</p>}
                  {k.na && <p className="text-[11px] text-slate-500">{k.naReason}</p>}
                  {k.omitted && (
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-800 border-indigo-200 text-[10px] mt-1" data-testid="kpi-paygap-omitted">
                      OMITTED_ALLOWED (&lt;{k.omissionThreshold} dip.)
                    </Badge>
                  )}
                  {k.estimated && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px] mt-1">FTE STIMATO</Badge>
                  )}
                  {k.metric && calcByMetric[k.metric] && (
                    <button
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 hover:underline"
                      onClick={() => setDrilldown(calcByMetric[k.metric])}
                      data-testid={`drilldown-${k.key}`}>
                      <Calculator className="w-3 h-3" aria-hidden="true" /> Come è stato calcolato?
                    </button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="social-modules">
            {Object.entries(s.modules || {}).map(([code, st]) => (
              <Card key={code} className="border-slate-200" data-testid={`module-card-${code}`}>
                <CardContent className="p-3">
                  <p className="text-xs font-bold text-slate-700">{code}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-2 rounded-full bg-slate-100 border border-slate-200">
                      <div className="h-full rounded-full bg-sky-600" style={{ width: `${st.completion}%` }} />
                    </div>
                    <span className="text-xs font-bold" data-testid={`module-${code}-completion`}>{st.completion}%</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">{st.completed}/{st.applicable} · {st.missing} mancanti</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {s.alerts?.length > 0 && (
            <div className="space-y-2" data-testid="social-alerts">
              {s.alerts.map((a, i) => (
                <div key={a.message} className={`flex items-start gap-2 rounded-md border p-3 text-sm ${a.severity === "WARNING" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-sky-50 border-sky-200 text-sky-900"}`}
                  data-testid={`social-alert-${i}`}>
                  {a.severity === "WARNING"
                    ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    : <Info className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />}
                  <span>{a.message}</span>
                </div>
              ))}
            </div>
          )}

          {s.missing?.length > 0 && (
            <Card className="border-slate-200" data-testid="social-missing">
              <CardContent className="p-4">
                <p className="text-sm font-semibold text-slate-800 mb-2">Dati Social mancanti</p>
                <ul className="space-y-1">
                  {s.missing.map((m) => (
                    <li key={m.code} className="text-sm text-slate-600 flex items-center gap-2" data-testid={`missing-${m.code}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      {m.message}
                      <span className="text-xs text-slate-400">({m.module} · questionario: {m.section})</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {s.comparison && (
            <Card className="border-slate-200" data-testid="social-comparison">
              <CardContent className="p-4">
                <p className="text-sm font-semibold text-slate-800 mb-2">Confronto con {s.comparison.year}</p>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                  {[["Workforce", s.comparison.workforce_delta_pct], ["Turnover", s.comparison.turnover_delta_pct],
                    ["Tasso infortuni", s.comparison.accident_rate_delta_pct], ["Formazione", s.comparison.training_delta_pct],
                    ["Pay gap", s.comparison.pay_gap_delta_pct], ["Contrattazione", s.comparison.cb_delta_pct]].map(([label, d]) => (
                    <div key={label}>
                      <p className="text-xs text-slate-500">{label}</p>
                      <p className="font-semibold text-slate-900">{d === null ? <span className="text-xs font-medium text-slate-500">Non disponibile</span> : `${d > 0 ? "+" : ""}${d}%`}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">Variazioni quantitative senza valutazione automatica di segno positivo/negativo.</p>
              </CardContent>
            </Card>
          )}

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex flex-wrap h-auto gap-1" data-testid="social-tabs">
              <TabsTrigger value="forza-lavoro" data-testid="tab-forza-lavoro">B8 — Forza lavoro</TabsTrigger>
              <TabsTrigger value="turnover" data-testid="tab-turnover">B8 — Turnover</TabsTrigger>
              <TabsTrigger value="sicurezza" data-testid="tab-sicurezza">B9 — Salute e sicurezza</TabsTrigger>
              <TabsTrigger value="retribuzioni" data-testid="tab-retribuzioni">B10 — Retribuzioni</TabsTrigger>
              <TabsTrigger value="formazione" data-testid="tab-formazione">B10 — Formazione</TabsTrigger>
              <TabsTrigger value="c5" data-testid="tab-c5">C5 — Caratteristiche aggiuntive</TabsTrigger>
              <TabsTrigger value="c6" data-testid="tab-c6">C6 — Policy diritti umani</TabsTrigger>
              <TabsTrigger value="c7" data-testid="tab-c7">C7 — Incidenti diritti umani</TabsTrigger>
            </TabsList>

            <TabsContent value="forza-lavoro" className="space-y-4 mt-4">
              <SingletonSection companyId={companyId} periodId={period.id} endpoint="workforce-snapshot"
                title="Workforce Snapshot" testid="social-workforce-snapshot" canWrite={canWrite} onChanged={setSummary}
                description="Fotografia della forza lavoro del periodo: headcount e FTE sono due misure diverse, mai mescolate."
                fields={[
                  { key: "calculation_basis", label: "Base di calcolo report", type: "select", required: true,
                    options: [{ value: "HEADCOUNT", label: "Headcount" }, { value: "FTE", label: "Full-Time Equivalent" }] },
                  { key: "headcount_total", label: "Headcount totale", type: "number", required: true },
                  { key: "fte_total", label: "FTE totali", type: "number" },
                  { key: "fte_method", label: "Metodo FTE (ore programmate / ore standard)", type: "text" },
                  { key: "fte_estimated", label: "FTE stimato", type: "boolean", hint: "Marcato ESTIMATED solo con metodologia definita." },
                ]} />
              <ListSection companyId={companyId} periodId={period.id} endpoint="workforce-breakdowns"
                title="Ripartizioni" testid="social-breakdowns" canWrite={canWrite} onChanged={setSummary}
                description="Ripartizione per contratto, genere, Paese del contratto e non-dipendenti (C5, mai sommati ai dipendenti B8)."
                addLabel="Aggiungi ripartizione"
                columns={[
                  { key: "dimension_type", label: "Dimensione", render: (r) => DIMENSION_OPTIONS.find((o) => o.value === r.dimension_type)?.label || r.dimension_type },
                  { key: "dimension_value", label: "Valore" },
                  { key: "headcount", label: "Headcount" },
                  { key: "fte", label: "FTE", render: (r) => r.fte ?? "—" },
                ]}
                fields={[
                  { key: "dimension_type", label: "Dimensione", type: "select", required: true, options: DIMENSION_OPTIONS },
                  { key: "dimension_value", label: "Valore (es. PERMANENT, FEMALE, IT, SELF_EMPLOYED_EXCLUSIVE)", type: "text", required: true,
                    hint: "Contratto: PERMANENT/TEMPORARY/OTHER/TO_REVIEW · Genere: FEMALE/MALE/OTHER/NOT_REPORTED · Paese: codice ISO (IT, FR…) · C5: SELF_EMPLOYED_EXCLUSIVE/TEMPORARY_AGENCY_WORKER" },
                  { key: "headcount", label: "Headcount", type: "number", required: true },
                  { key: "fte", label: "FTE", type: "number" },
                  { key: "notes", label: "Note", type: "text" },
                ]} />
            </TabsContent>

            <TabsContent value="turnover" className="mt-4">
              <SingletonSection companyId={companyId} periodId={period.id} endpoint="workforce-movements"
                title="Movimenti del personale" testid="social-movements" canWrite={canWrite} onChanged={setSummary}
                description="Turnover VSME = uscite ÷ dipendenti medi × 100. Le uscite includono dimissioni, licenziamenti, pensionamenti e decessi in servizio. Richiesto sopra 50 dipendenti."
                fields={[
                  { key: "hires", label: "Assunzioni", type: "number" },
                  { key: "leavers", label: "Uscite totali", type: "number", required: true },
                  { key: "voluntary_leavers", label: "di cui dimissioni", type: "number" },
                  { key: "involuntary_leavers", label: "di cui licenziamenti", type: "number" },
                  { key: "retirement_leavers", label: "di cui pensionamenti", type: "number" },
                  { key: "death_in_service_leavers", label: "di cui decessi in servizio", type: "number" },
                  { key: "average_employees", label: "Dipendenti medi nel periodo", type: "number", required: true },
                  { key: "methodology", label: "Metodologia dipendenti medi", type: "text", hint: "Es. media degli headcount mensili." },
                ]} />
            </TabsContent>

            <TabsContent value="sicurezza" className="space-y-4 mt-4">
              <SingletonSection companyId={companyId} periodId={period.id} endpoint="safety-summary"
                title="Eventi di sicurezza (aggregati)" testid="social-safety" canWrite={canWrite} onChanged={setSummary}
                description="Solo infortuni sul lavoro REGISTRABILI secondo la definizione VSME. Zero è un dato valido. Nessun dettaglio sanitario o nominativo viene memorizzato."
                fields={[
                  { key: "recordable_accidents", label: "Infortuni registrabili", type: "number", required: true },
                  { key: "fatalities_injuries", label: "Decessi da infortunio sul lavoro", type: "number", required: true },
                  { key: "fatalities_ill_health", label: "Decessi da malattia professionale", type: "number" },
                  { key: "ill_health_cases", label: "Casi di malattia professionale", type: "number" },
                  { key: "notes", label: "Note", type: "textarea", full: true },
                ]} />
              <SingletonSection companyId={companyId} periodId={period.id} endpoint="hours-worked"
                title="Ore lavorate totali" testid="social-hours" canWrite={canWrite} onChanged={setSummary}
                description="Necessarie per il tasso infortuni (infortuni ÷ ore × 200.000). Se stimate, marcatura ESTIMATED con metodologia."
                fields={[
                  { key: "total_hours", label: "Ore lavorate totali nell'anno", type: "number", required: true },
                  { key: "estimated", label: "Dato stimato", type: "boolean" },
                  { key: "method", label: "Metodo/stima utilizzata", type: "text" },
                ]} />
            </TabsContent>

            <TabsContent value="retribuzioni" className="space-y-4 mt-4">
              <SingletonSection companyId={companyId} periodId={period.id} endpoint="remuneration-summary"
                title="Retribuzioni (aggregate)" testid="social-remuneration" canWrite={canWrite} onChanged={setSummary}
                description="Solo medie orarie lorde aggregate per genere: nessun cedolino individuale. Pay gap = (media uomini − media donne) ÷ media uomini × 100."
                fields={[
                  { key: "minimum_wage_status", label: "Retribuzione ≥ minimo applicabile (legge o CCNL)?", type: "select",
                    options: [{ value: "YES", label: "Sì" }, { value: "NO", label: "No" }, { value: "TO_VERIFY", label: "Da verificare" }] },
                  { key: "avg_male_hourly", label: "Retribuzione oraria lorda media uomini (€/h)", type: "number" },
                  { key: "avg_female_hourly", label: "Retribuzione oraria lorda media donne (€/h)", type: "number" },
                  { key: "notes", label: "Note metodologiche", type: "textarea", full: true },
                ]} />
              <SingletonSection companyId={companyId} periodId={period.id} endpoint="collective-bargaining"
                title="Contrattazione collettiva" testid="social-cb" canWrite={canWrite} onChanged={setSummary}
                description="Copertura = dipendenti coperti da contratti collettivi ÷ totale dipendenti × 100. Non è l'iscrizione sindacale né la rappresentanza."
                fields={[
                  { key: "covered_employees", label: "Dipendenti coperti da CCNL", type: "number", required: true },
                  { key: "total_employees", label: "Totale dipendenti (vuoto = usa headcount)", type: "number" },
                  { key: "agreement_reference", label: "CCNL applicato (riferimento)", type: "text" },
                ]} />
            </TabsContent>

            <TabsContent value="formazione" className="mt-4">
              <ListSection companyId={companyId} periodId={period.id} endpoint="training-summaries"
                title="Formazione per genere" testid="social-training" canWrite={canWrite} onChanged={setSummary}
                description="Ore medie = ore totali di formazione della categoria ÷ dipendenti della categoria. Le ore di uno stesso dipendente su più corsi si sommano."
                addLabel="Aggiungi categoria"
                columns={[
                  { key: "gender_category", label: "Categoria", render: (r) => GENDER_OPTIONS.find((o) => o.value === r.gender_category)?.label || r.gender_category },
                  { key: "employee_count", label: "Dipendenti" },
                  { key: "total_training_hours", label: "Ore totali" },
                  { key: "avg", label: "Media", render: (r) => (r.employee_count ? fmt(r.total_training_hours / r.employee_count, " h") : "—") },
                ]}
                fields={[
                  { key: "gender_category", label: "Categoria", type: "select", required: true, options: GENDER_OPTIONS },
                  { key: "employee_count", label: "Dipendenti della categoria", type: "number" },
                  { key: "total_training_hours", label: "Ore totali di formazione", type: "number", required: true },
                  { key: "notes", label: "Note", type: "text" },
                ]} />
            </TabsContent>

            <TabsContent value="c5" className="space-y-4 mt-4">
              <SingletonSection companyId={companyId} periodId={period.id} endpoint="management-summary"
                title="Management gender ratio" testid="social-management" canWrite={canWrite} onChanged={setSummary}
                description="Rapporto donne/uomini al livello management (standard: livello sotto il Board, salvo definizione aziendale). Denominatore zero → rapporto non calcolabile, valori assoluti visibili."
                fields={[
                  { key: "female_management", label: "Donne nel management", type: "number" },
                  { key: "male_management", label: "Uomini nel management", type: "number" },
                  { key: "other_not_reported", label: "Altro / non dichiarato", type: "number" },
                  { key: "management_definition", label: "Definizione di management", type: "select",
                    options: [{ value: "STANDARD_DEFINITION", label: "Definizione standard VSME" }, { value: "COMPANY_DEFINED", label: "Definizione aziendale" }] },
                  { key: "definition_description", label: "Descrizione (se definizione aziendale)", type: "textarea", full: true },
                ]} />
              <p className="text-xs text-slate-500">
                I non-dipendenti C5 (autonomi esclusivi, somministrati) si inseriscono nella tab Forza lavoro con dimensione
                "Non-dipendenti (C5)": restano popolazioni distinte dai dipendenti B8.
              </p>
            </TabsContent>

            <TabsContent value="c6" className="mt-4">
              {canHR ? (
                <SingletonSection companyId={companyId} periodId={period.id} endpoint="hr-policy"
                  title="Policy diritti umani e meccanismo di reclami" testid="social-hr-policy" canWrite={canWrite} onChanged={setSummary}
                  description="Codice di Condotta o policy diritti umani sulla forza lavoro. Ogni ambito coperto è una voce separata. La presenza di un documento non equivale automaticamente a una policy implementata: serve conferma."
                  fields={[
                    { key: "code_or_policy_exists", label: "L'azienda dispone di un Codice di Condotta / policy diritti umani?", type: "boolean", required: true },
                    { key: "child_labour", label: "Copre: lavoro minorile", type: "boolean" },
                    { key: "forced_labour", label: "Copre: lavoro forzato", type: "boolean" },
                    { key: "human_trafficking", label: "Copre: tratta di esseri umani", type: "boolean" },
                    { key: "discrimination", label: "Copre: discriminazione", type: "boolean" },
                    { key: "accident_prevention", label: "Copre: prevenzione infortuni", type: "boolean" },
                    { key: "other", label: "Copre: altro", type: "boolean" },
                    { key: "other_description", label: "Specifica altro", type: "text" },
                    { key: "complaints_mechanism", label: "Meccanismo di gestione reclami della forza lavoro", type: "boolean",
                      hint: "Whistleblowing non equivale automaticamente a un meccanismo di reclami." },
                  ]} />
              ) : (
                <RestrictedCard testid="c6-restricted" />
              )}
            </TabsContent>

            <TabsContent value="c7" className="mt-4">
              {canHR ? (
                <SingletonSection companyId={companyId} periodId={period.id} endpoint="hr-incidents"
                  title="Incidenti confermati sui diritti umani" testid="social-hr-incidents" canWrite={canWrite} onChanged={setSummary}
                  description="Solo incidenti CONFERMATI secondo la metodologia applicabile: segnalazioni non verificate o rumor non sono incidenti. 'No' è un dato valido e completo. L'AI non può mai dichiarare automaticamente un incidente."
                  fields={[
                    { key: "own_workforce_status", label: "Incidenti confermati sulla propria forza lavoro?", type: "select", required: true,
                      options: [{ value: "YES", label: "Sì" }, { value: "NO", label: "No" }, { value: "TO_VERIFY", label: "Da verificare" }] },
                    { key: "confirmed_count", label: "Numero di incidenti confermati (se disponibile)", type: "number" },
                    { key: "actions_taken", label: "Azioni intraprese (descrizione manuale)", type: "textarea", full: true },
                    { key: "value_chain_status", label: "Incidenti confermati noti su catena del valore, comunità, consumatori?", type: "select",
                      options: [{ value: "YES", label: "Sì" }, { value: "NO", label: "No" }] },
                    { key: "value_chain_specify", label: "Specifica (se sì)", type: "textarea", full: true },
                  ]} />
              ) : (
                <RestrictedCard testid="c7-restricted" />
              )}
            </TabsContent>
          </Tabs>
        </>
      )}

      <KpiDrilldown calc={drilldown} onClose={() => setDrilldown(null)} />

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="social-import-dialog">
          <DialogHeader>
            <DialogTitle>Importa dati Social aggregati (CSV)</DialogTitle>
            <DialogDescription>
              Colonne: metric_code, value, unit, notes. Solo dati aggregati: colonne con dati personali
              (nome, cognome, codice fiscale…) vengono rilevate e ignorate con un avviso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded p-2">
              metric_code,value{"\n"}WORKFORCE_TOTAL,80{"\n"}WORKFORCE_FEMALE,35{"\n"}TOTAL_HOURS_WORKED,80000
            </p>
            <Textarea rows={6} value={csvText} onChange={(e) => setCsvText(e.target.value)}
              placeholder="Incolla qui il CSV…" data-testid="social-import-csv" />
            {importPreview && (
              <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="social-import-preview">
                <p className="text-sm font-semibold text-slate-800">
                  Righe: {importPreview.total_rows} · Valide: {importPreview.valid} · Errori: {importPreview.errors.length}
                </p>
                {importPreview.pii_warning && (
                  <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900" data-testid="import-pii-warning">
                    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{importPreview.pii_warning}</span>
                  </div>
                )}
                {importPreview.errors.map((e) => (
                  <p key={e.row} className="text-xs text-red-700">Riga {e.row}: {e.error}</p>
                ))}
                {importPreview.rows.slice(0, 10).map((r) => (
                  <p key={r.row} className="text-xs text-slate-600">✓ {r.metric_code} → {r.value} {r.unit}</p>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => runImport(false)} disabled={!csvText.trim()} data-testid="social-import-preview-btn">Anteprima</Button>
            <Button className="bg-green-700 hover:bg-green-800"
              disabled={!importPreview || importPreview.valid === 0}
              onClick={() => runImport(true)} data-testid="social-import-commit-btn">
              Conferma import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RestrictedCard({ testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-6 text-sm text-slate-600 flex items-start gap-2">
        <ShieldAlert className="w-5 h-5 text-slate-400 shrink-0" aria-hidden="true" />
        <span>
          Sezione riservata: i dati sensibili sui diritti umani richiedono permessi dedicati
          (social:hr). Il Super Admin piattaforma non vi accede per privacy by design.
        </span>
      </CardContent>
    </Card>
  );
}
